/**
 * 작성자 : 박승호
 * 날짜 : 2024.11.28
 * 기능 : 파일 확장자 이미지 출력
 */

import { ReactComponent as Normal } from '../assets/images/icons/format/ico_file.svg';
import { ReactComponent as Pdf } from '../assets/images/icons/format/ico_pdf.svg';
import { ReactComponent as Xls } from '../assets/images/icons/format/ico_xls.svg';
import { ReactComponent as Txt } from '../assets/images/icons/format/ico_txt.svg';
import { ReactComponent as Ppt } from '../assets/images/icons/format/ico_ppt.svg';
import { ReactComponent as Doc } from '../assets/images/icons/format/ico_doc.svg';
import { ReactComponent as Jpg } from '../assets/images/icons/format/ico_jpg.svg';
import { ReactComponent as Png } from '../assets/images/icons/format/ico_png.svg';
import { ReactComponent as Gif } from '../assets/images/icons/format/ico_gif.svg';
import { ReactComponent as Bmp } from '../assets/images/icons/format/ico_bmp.svg';
import styled from 'styled-components';

const FileTextIconRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 0.25rem;
  background: #d3d3d3;
  color: #414141;
  width: 3rem;
  height: 3rem;

  &.sm {
    width: 2.4rem;
    height: 2.4rem;
  }

  &.md {
    width: 3rem;
    height: 3rem;
  }

  &.pdf {
    background: #ffd9d9;
    color: #de3434;
  }

  &.xls,
  &.xlsx {
    background: #d8ffd9;
    color: #18a128;
  }

  &.txt {
    background: #cee3ff;
    color: #2b6ec5;
  }

  &.ppt,
  &.pptx {
    background: #ffe3ca;
    color: #de7111;
  }

  &.doc,
  &.docx {
    background: #daedff;
    color: #005c8b;
  }

  &.jpg,
  &.jpeg {
    background: #d8deff;
    color: #1c35b9;
  }

  &.png {
    background: #ffe8b5;
    color: #9f6e00;
  }

  &.gif {
    background: #e2eaff;
    color: #3756a7;
  }

  &.bmp {
    background: #edd8ff;
    color: #a11fdd;
  }

  &.mp4 {
    background: #e8d5ff;
    color: #43008b;
  }

  &.mp3 {
    background: #ffd7e5;
    color: #c52b64;
  }
`;

interface Props {
  format: string; // 파일 확장자명
  size?: number; // svg 사이즈
  type?: 'icon' | 'text'; // 타입
  textSize?: 'sm' | 'md' | 'lg'; // 타입이 텍스트인 크기
}

const FormatImg = ({ format, size = 28, type = 'icon', textSize = 'md' }: Props) => {
  const renderFormatSvg = (fmt: string) => {
    switch (fmt) {
      case 'pdf':
        return <Pdf width={size} height={size} />;
      case 'xls':
      case 'xlsx':
        return <Xls width={size} height={size} />;
      case 'txt':
        return <Txt width={size} height={size} />;
      case 'ppt':
      case 'pptx':
        return <Ppt width={size} height={size} />;
      case 'doc':
      case 'docx':
        return <Doc width={size} height={size} />;
      case 'jpg':
      case 'jpeg':
        return <Jpg width={size} height={size} />;
      case 'png':
        return <Png width={size} height={size} />;
      case 'gif':
        return <Gif width={size} height={size} />;
      case 'bmp':
        return <Bmp width={size} height={size} />;
      default:
        return <Normal width={size} height={size} />;
    }
  };

  const renderFormatText = (fmt: string) => {
    if (fmt === 'pdf') return 'PDF';
    if (fmt === 'xls' || fmt === 'xlsx') return 'XLS';
    if (fmt === 'txt') return 'TXT';
    if (fmt === 'ppt' || fmt === 'pptx') return 'PPT';
    if (fmt === 'doc' || fmt === 'docx') return 'DOC';
    if (fmt === 'jpg' || fmt === 'jpeg') return 'JPG';
    if (fmt === 'png') return 'PNG';
    if (fmt === 'gif') return 'GIF';
    if (fmt === 'bmp') return 'BMP';
    if (fmt === 'mp4') return 'MP4';
    if (fmt === 'mp3') return 'MP3';
    return 'FILE'; // 기본값
  };

  if (type === 'text') return <FileTextIconRoot className={`${format.toLowerCase() || 'file'} ${textSize}`}>{renderFormatText(format.toLowerCase() || 'file')}</FileTextIconRoot>;

  return renderFormatSvg(format.toLowerCase());
};

export default FormatImg;
